import { Component, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import i18next from 'i18next'
import { Button } from '@/components/ui/button'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

// Custom error boundary that works with or without Sentry
class ErrorBoundaryBase extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="text-center space-y-4 max-w-md">
            <div className="text-6xl">😵</div>
            <h1 className="text-2xl font-bold">{i18next.t('errorBoundary.title')}</h1>
            <p className="text-muted-foreground">
              {i18next.t('errorBoundary.description')}
            </p>
            {this.state.error && import.meta.env.DEV && (
              <pre className="text-left text-xs bg-muted p-4 rounded-lg overflow-auto max-h-40">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex gap-2 justify-center">
              <Button onClick={this.handleReset}>{i18next.t('errorBoundary.tryAgain')}</Button>
              <Button variant="outline" onClick={() => window.location.reload()}>
                {i18next.t('errorBoundary.reloadPage')}
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Fallback component for Sentry error boundary
function SentryFallback({
  error,
  resetError,
}: {
  error: unknown
  componentStack: string
  eventId: string
  resetError: () => void
}) {
  const errorMessage = error instanceof Error ? error.message : 'Unknown error'

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="text-center space-y-4 max-w-md">
        <div className="text-6xl">😵</div>
        <h1 className="text-2xl font-bold">{i18next.t('errorBoundary.title')}</h1>
        <p className="text-muted-foreground">
          {i18next.t('errorBoundary.descriptionWithSentry')}
        </p>
        {import.meta.env.DEV && (
          <pre className="text-left text-xs bg-muted p-4 rounded-lg overflow-auto max-h-40">
            {errorMessage}
          </pre>
        )}
        <div className="flex gap-2 justify-center">
          <Button onClick={resetError}>{i18next.t('errorBoundary.tryAgain')}</Button>
          <Button variant="outline" onClick={() => window.location.reload()}>
            {i18next.t('errorBoundary.reloadPage')}
          </Button>
        </div>
      </div>
    </div>
  )
}

// Use Sentry's error boundary if available, otherwise use our custom one
export const ErrorBoundary = import.meta.env.VITE_SENTRY_DSN
  ? Sentry.withErrorBoundary(ErrorBoundaryBase, {
      fallback: SentryFallback,
      showDialog: false, // We use our own UI
    })
  : ErrorBoundaryBase

export default ErrorBoundary
